import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from './AddressSearch.module.scss';
import { searchAddress } from '@src/api/Referencial';
import Input from '../Input';
import ChrevronRight from '@src/assets/ChevronRight.png';
import { AddressEnumType, SearchAddressType } from '@src/utilities/types';
import Loader from '../Loader';

type AddressSearchProps = {
    selectedAddress: any;
    _onSelectExistingAddress: (searchAddress: SearchAddressType) => void;
    setNotFound: React.Dispatch<React.SetStateAction<boolean>>;
    setAddress: React.Dispatch<React.SetStateAction<any>>
}

const AddressSearch: React.FC<AddressSearchProps> = ({ selectedAddress, _onSelectExistingAddress, setNotFound, setAddress }) => {
    const [query, setQuery] = useState(`${selectedAddress.street_number ? selectedAddress.street_number?.toString() : ''}${selectedAddress.street_extension ? selectedAddress.street_extension?.toString() : ''}${selectedAddress.street_name_further?.toString()} ${selectedAddress.street_name?.toString()} ${selectedAddress.city.zipcode?.toString()} ${selectedAddress.city.name?.toString()}`);

    const [debouncedQuery, setDebouncedQuery] = useState<string>('');
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState<boolean>(false);
    const [selected, setSelected] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const { data: suggestions, refetch, isLoading } = useQuery({
        queryKey: [`searchAddress`, debouncedQuery],
        queryFn: () => searchAddress({ searchValue: debouncedQuery }),
        enabled: false
    });

    useEffect(() => {
        if (debouncedQuery && debouncedQuery.trim() !== '' && !selected) {
            refetch();
            setIsSuggestionsOpen(true);
        } else {
            setIsSuggestionsOpen(false);
        }
    }, [debouncedQuery, refetch, selected]);

    const _onSelectAddress = (suggestion: any) => {
        _onSelectExistingAddress(suggestion)
        setQuery(suggestion.label);
        setIsSuggestionsOpen(false);
        setDebouncedQuery('');
        setSelected(true);
    }

    const _onClear = () => {
        setAddress({
            address_type: AddressEnumType.postale,
            street_number: '',
            street_extension: '',
            street_name: '',
            street_name_further: '',
            city: {
                zipcode: '',
                name: ''
            },
            country: selectedAddress.country,
            id: ''
        },)
        setQuery('');
        setIsSuggestionsOpen(false);
        setDebouncedQuery('');
        setSelected(false);
    }

    const _notFound = () => {
        setNotFound(true);
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsSuggestionsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const renderSuggestions = (): JSX.Element | null => {
        if (!isSuggestionsOpen) return null;
        
        if (isLoading) {
            return (
                <ul className={styles.suggestions}>
                    <li className={styles.suggestionItem} style={{ display: 'flex', justifyContent: 'center'}}>
                        <Loader type="SMALL_SPINNER" />
                    </li>
                </ul>
            );
        }
        
        if (!suggestions || suggestions.length === 0) {
            return (
                <ul className={styles.suggestions}>
                    <li>
                        <button className={styles.notFoundButton} onClick={_notFound}>
                            <p className={styles.notFoundText}>Mon adresse n'est pas ici</p>
                            <img src={ChrevronRight} alt="ChrevronRight" width={7} height={12} />
                        </button>
                    </li>
                    <li className={styles.errorMessage}>Nous ne parvenons pas à afficher votre adresse, mais pas d'inquiétude, vous pouvez la saisir manuellement</li>
                </ul>
            );
        }
        
        return (
            <ul className={styles.suggestions}>
                <li>
                    <button className={styles.notFoundButton} onClick={_notFound} type="button" >
                        <p className={styles.notFoundText}>Mon adresse n'est pas ici</p>
                        <img src={ChrevronRight} alt="ChrevronRight" width={7} height={12} />
                    </button>
                </li>
                {suggestions.map((suggestion: any, index) => (
                    <li
                        key={index}
                        onClick={() => _onSelectAddress(suggestion)}
                        className={styles.suggestionItem}
                    >
                        {suggestion.label}
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div ref={containerRef} className={styles.container}>
            <Input
                title="Recherchez votre adresse"
                type="text"
                width="100%"
                isSearch
                value={query.trim() === '' ? '' : query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ex : 13 rue de la Colombe 75001 Paris"
                clearInput={_onClear}
                disabledAddress={selected}
            />
            {renderSuggestions()}
        </div>
    );
};

export default AddressSearch;
